import { Badge } from "@/components/ui/badge";

// P5B.2 Part 1/2 — dedicated renderer for the structured Reports AI-summary
// schema. Never uses dangerouslySetInnerHTML; every piece of text is plain
// React text content (React escapes it by construction). Numeric facts
// (metric_facts) are backend-owned and rendered SEPARATELY from the
// model-authored narrative — the model's own text never carries a number.
const SEVERITY_STYLES = {
  positive: "border-l-2 border-[hsl(var(--success))] pl-2",
  attention: "border-l-2 border-[hsl(var(--destructive))] pl-2",
  neutral: "border-l-2 border-muted-foreground/30 pl-2",
};
const SEVERITY_LABEL = { positive: "Positive", attention: "Needs attention", neutral: "Neutral" };

function MetricValue({ fact }) {
  if (!fact) return null;
  const display = fact.state === "not_configured" ? "Not configured"
    : fact.state === "unknown" ? "Unknown" : fact.value;
  return (
    <span data-testid="report-ai-metric-value" className="text-xs font-semibold font-mono">
      {fact.label}: {display}{fact.unit && fact.state === "value" ? ` ${fact.unit}` : ""}
    </span>
  );
}

function KeySignalRow({ signal, metricFacts }) {
  const fact = metricFacts?.[signal.metric_key];
  return (
    <div className={SEVERITY_STYLES[signal.severity] || SEVERITY_STYLES.neutral} data-testid="report-ai-key-signal">
      <div className="flex items-center gap-2 flex-wrap">
        <MetricValue fact={fact} />
        <Badge variant="outline" className="text-[9px]">{SEVERITY_LABEL[signal.severity] || signal.severity}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{signal.narrative}</p>
    </div>
  );
}

export function ReportAiSummaryContent({ summary }) {
  if (!summary) return null;

  if (summary.content_status === "legacy_corrupted") {
    return (
      <p data-testid="report-ai-corrupted" className="text-xs text-destructive">
        This older AI content cannot be displayed safely. Regenerate it to create a verified version.
      </p>
    );
  }

  if (summary.content_status === "legacy_safe") {
    return (
      <div className="space-y-1" data-testid="report-ai-legacy">
        <Badge variant="outline" className="text-[9px]">Legacy</Badge>
        <p className="text-sm whitespace-pre-wrap">{summary.legacy_plain_text}</p>
      </div>
    );
  }

  // Structured (schema_version present) — the only format newly generated.
  return (
    <div className="space-y-3" data-testid="report-ai-structured">
      <p data-testid="report-ai-overview" className="text-sm">{summary.overview}</p>
      {(summary.key_signals || []).length > 0 && (
        <div className="space-y-2">
          {summary.key_signals.map((sig, i) => (
            <KeySignalRow key={i} signal={sig} metricFacts={summary.metric_facts} />
          ))}
        </div>
      )}
      {(summary.recommended_actions || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground">Recommended actions</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5">
            {summary.recommended_actions.map((a, i) => <li key={i}>{a.text}</li>)}
          </ul>
        </div>
      )}
      {(summary.caveats || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground">Caveats</div>
          <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5">
            {summary.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
